import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
} from '@mui/material';
import { AttachMoney } from '@mui/icons-material';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

interface TV {
  id: string;
  name: string;
  price?: number;
  customName?: string;
}

const SettingsPricePage = () => {
  const [tvs, setTvs] = useState<TV[]>([]);
  const [loading, setLoading] = useState(true);
  const [defaultPrice, setDefaultPrice] = useState<number>(8000);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTV, setEditingTV] = useState<TV | null>(null);
  const [editPrice, setEditPrice] = useState<number>(0);

  useEffect(() => {
    fetchTVs();
    fetchDefaultPrice();
  }, []);

  const fetchTVs = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/sheets/tvs`);
      if (response.ok) {
        const data = await response.json() as { tvs: TV[] };
        setTvs(data.tvs || []);
      }
    } catch (error) {
      console.error('Error fetching TVs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDefaultPrice = async () => {
    // TODO: Fetch default price from API if available
    setDefaultPrice(8000); // fallback
  };

  const handleEdit = (tv: TV) => {
    setEditingTV(tv);
    setEditPrice(tv.price || defaultPrice);
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingTV) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/sheets/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: editingTV.id, price: editPrice }),
      });
      if (response.ok) {
        fetchTVs();
        setEditDialogOpen(false);
        setEditingTV(null);
      }
    } catch (error) {
      console.error('Error updating price:', error);
    }
  };

  const handleDefaultPriceSave = async () => {
    // TODO: Save default price to API
    // For now, just keep it in state
  };

  return (
    <Box sx={{ p: 4, bgcolor: '#f5f5f7', minHeight: '100vh' }}>
      <Typography variant="h4" fontWeight={700} sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
        <AttachMoney sx={{ color: '#3730f2', fontSize: 32, mr: 1 }} /> Price Settings
      </Typography>
      <Paper sx={{ p: 3, mb: 4, borderRadius: 4 }}>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
          Default Price per Hour
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TextField
            label="Price per Hour (Rp)"
            type="number"
            value={defaultPrice}
            onChange={e => setDefaultPrice(Number(e.target.value))}
            InputProps={{ startAdornment: <InputAdornment position="start">Rp</InputAdornment> }}
            sx={{ width: 200 }}
          />
          <Button variant="contained" sx={{ bgcolor: '#3730f2' }} onClick={handleDefaultPriceSave}>
            Save
          </Button>
        </Box>
      </Paper>
      <Paper sx={{ p: 3, borderRadius: 4 }}>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
          <span role="img" aria-label="tv">📺</span> TV Billing List - Price Management
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Custom Name</TableCell>
                <TableCell>Current Price/Hour</TableCell>
                <TableCell>Edit Price</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tvs.map(tv => (
                <TableRow key={tv.id}>
                  <TableCell>{tv.customName || tv.name}</TableCell>
                  <TableCell sx={{ color: '#3730f2', fontWeight: 600 }}>
                    {tv.price ? `Rp${tv.price.toLocaleString()}` : `Rp${defaultPrice.toLocaleString()}`}
                  </TableCell>
                  <TableCell>
                    <IconButton 
                      color="primary" 
                      onClick={() => handleEdit(tv)}
                      sx={{
                        bgcolor: 'rgba(55,48,242,0.08)',
                        borderRadius: '50%',
                        p: 1,
                        transition: 'background 0.2s',
                        '&:hover': { bgcolor: 'rgba(55,48,242,0.18)' },
                      }}
                    >
                      <AttachMoney />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <DialogTitle>Edit Price</DialogTitle>
        <DialogContent>
          <TextField
            label="Price per Hour (Rp)"
            type="number"
            value={editPrice}
            onChange={e => setEditPrice(Number(e.target.value))}
            InputProps={{ startAdornment: <InputAdornment position="start">Rp</InputAdornment> }}
            sx={{ mt: 1, width: 220 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained" sx={{ bgcolor: '#3730f2' }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SettingsPricePage; 